import { getBirthDateValidationError } from "@/utils/birth-date-input";
import { FAN_DIGIT_COUNT, isValidFanId, normalizeFanId } from "@/utils/fan-id";
import { PreserviceProgramTypeEnum, YearOfEducEnum } from "@/utils/enums";
import {
  DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  GENERIC_ENTRY_TEMPLATE_HEADERS,
  LEGACY_DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  LEGACY_GENERIC_ENTRY_TEMPLATE_HEADERS,
  LEGACY_TVET_TEMPLATE_HEADERS,
  PRESERVICE_STATUS_SHEET_NAME,
  PRESERVICE_STATUS_TEMPLATE_HEADERS,
  TVET_TEMPLATE_HEADERS,
} from "@/components/Graduates/preservice-import-contract";

export {
  DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  GENERIC_ENTRY_TEMPLATE_HEADERS,
  LEGACY_DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  LEGACY_GENERIC_ENTRY_TEMPLATE_HEADERS,
  LEGACY_TVET_TEMPLATE_HEADERS,
  PRESERVICE_STATUS_SHEET_NAME,
  PRESERVICE_STATUS_TEMPLATE_HEADERS,
  TVET_TEMPLATE_HEADERS,
};

const normalizeText = (value) => (value ?? "").toString().trim();

const normalizeLookupValue = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const toIsoDateString = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const milliseconds = Math.round(value * 24 * 60 * 60 * 1000);
    const parsedDate = new Date(excelEpoch.getTime() + milliseconds);

    if (!Number.isNaN(parsedDate.getTime())) {
      const year = parsedDate.getUTCFullYear();
      const month = String(parsedDate.getUTCMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  }

  return normalizeText(value);
};

const hasValidFanIdFormat = (value) => {
  const rawValue = normalizeText(value);
  if (!rawValue) {
    return false;
  }

  if (/[eE.]/.test(rawValue)) {
    return false;
  }

  if (!/^[\d\s-]+$/.test(rawValue)) {
    return false;
  }

  return normalizeFanId(rawValue).length === FAN_DIGIT_COUNT;
};

const getRowValue = (row = {}, aliases = []) => {
  const normalizedEntries = Object.entries(row).map(([key, value]) => [
    normalizeLookupValue(key),
    value,
  ]);

  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(row, alias)) {
      return row[alias];
    }
    const normalizedAlias = normalizeLookupValue(alias);
    const matchedEntry = normalizedEntries.find(
      ([key]) => key === normalizedAlias,
    );
    if (matchedEntry) {
      return matchedEntry[1];
    }
  }
  return "";
};

const findLookupItem = (items = [], rawValue, options = {}) => {
  const value = normalizeText(rawValue);
  if (!value) {
    return null;
  }

  const { fallbackKeys = [] } = options;
  const normalizedValue = normalizeLookupValue(value);

  return (
    items.find((item) => String(item?.id) === value) ||
    items.find(
      (item) => normalizeLookupValue(item?.name) === normalizedValue,
    ) ||
    items.find(
      (item) => normalizeLookupValue(item?.code) === normalizedValue,
    ) ||
    items.find((item) =>
      (Array.isArray(item?.aliases) ? item.aliases : []).some(
        (alias) => normalizeLookupValue(alias) === normalizedValue,
      ),
    ) ||
    items.find((item) =>
      fallbackKeys.some(
        (key) => normalizeLookupValue(item?.[key]) === normalizedValue,
      ),
    ) ||
    null
  );
};

const findProgramDiscipline = (disciplines = [], program, rawValue) => {
  const programDisciplines = program
    ? disciplines.filter((item) => item?.educationProgramId == program.id)
    : disciplines;

  return (
    findLookupItem(programDisciplines, rawValue) ||
    (!program ? findLookupItem(disciplines, rawValue) : null)
  );
};

const parseYear = (rawValue) => {
  const value = normalizeText(rawValue);
  if (!value) {
    return null;
  }

  if (!/^\d{4}$/.test(value)) {
    return { invalid: true };
  }

  const year = Number(value);
  if (Number.isNaN(year)) {
    return { invalid: true };
  }

  return year;
};

const parseNumber = (rawValue) => {
  const value = normalizeText(rawValue);
  if (!value) {
    return null;
  }

  const parsedValue = Number(value);
  if (Number.isNaN(parsedValue)) {
    return { invalid: true };
  }

  return parsedValue;
};

const toTitleCase = (value) => {
  const text = normalizeText(value);
  if (!text) {
    return "";
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const normalizeLegacyWithdrawalStatus = (value) => {
  const normalizedValue = normalizeText(value).toLowerCase();
  if (normalizedValue === "yes") {
    return "Withdrawn";
  }
  if (normalizedValue === "no") {
    return "Active";
  }
  return normalizeText(value);
};

export const formatAllowedValues = (values = []) =>
  values
    .map((value) => normalizeText(value))
    .filter(Boolean)
    .join(", ");

const getActiveLookupItems = (items = []) =>
  items.filter((item) => item?.isActive !== false);

const getUniqueLookupNames = (items = []) =>
  [...new Set(items.map((item) => item?.name).filter(Boolean))];

const getActiveUniversityOptions = (lookups = {}) =>
  getUniqueLookupNames(getActiveLookupItems(lookups.universities || []));

const YEAR_OF_EDUCATION_LEGACY_ALIASES = {
  "1styear": "1st Year 1st Semester",
};

const resolveYearOfEducationValue = (rawValue, options = []) => {
  const value = normalizeText(rawValue);
  if (!value) {
    return value;
  }

  const exactOption = options.find(
    (option) => normalizeLookupValue(option) === normalizeLookupValue(value),
  );
  if (exactOption) {
    return exactOption;
  }

  const legacyTarget =
    YEAR_OF_EDUCATION_LEGACY_ALIASES[normalizeLookupValue(value)];
  if (!legacyTarget) {
    return value;
  }

  return (
    options.find(
      (option) =>
        normalizeLookupValue(option) === normalizeLookupValue(legacyTarget),
    ) || legacyTarget
  );
};

const DEFAULT_GENERIC_ENTRY_NATIONALITY = "Ethiopia";

const buildSharedIdentityTemplateOptions = (lookups = {}) => {
  const activeItems = (items = []) => getActiveLookupItems(items);
  const activeNames = (items = []) => getUniqueLookupNames(activeItems(items));
  const birthRegions = activeItems(lookups.birthRegions || []);
  const birthZones = activeItems(lookups.birthZones || []);
  const birthWoredas = activeItems(lookups.birthWoredas || []);
  const ethnicityOptions = activeNames(lookups.ethnicities || []);
  const birthRegionOptions = getUniqueLookupNames(birthRegions);
  const birthZoneOptions = getUniqueLookupNames(birthZones);
  const birthWoredaOptions = getUniqueLookupNames(birthWoredas);

  return {
    nationalityDefault: DEFAULT_GENERIC_ENTRY_NATIONALITY,
    ethnicityOptions,
    birthRegionOptions,
    birthZoneOptions,
    birthWoredaOptions,
    birthHierarchy: {
      regions: birthRegions,
      zones: birthZones,
      woredas: birthWoredas,
    },
    identityMissingRequirements: {
      hasEthnicities: ethnicityOptions.length > 0,
      hasBirthRegions: birthRegionOptions.length > 0,
      hasBirthZones: birthZoneOptions.length > 0,
      hasBirthWoredas: birthWoredaOptions.length > 0,
    },
  };
};

const getGenericEntryDefaultLevel = (levels = []) =>
  levels.find(
    (item) =>
      normalizeLookupValue(item?.name) === normalizeLookupValue("Degree"),
  ) || null;

const pushError = (collection, rowNumber, message) => {
  if (!collection[rowNumber]) {
    collection[rowNumber] = [];
  }
  collection[rowNumber].push(message);
};

const finalizeValidation = (rows, errorsByRow) => ({
  validRows: rows,
  errors: errorsByRow,
  hasErrors: Object.keys(errorsByRow).length > 0,
});

export const validatePreserviceImportSource = ({
  fileName = "",
  headers = [],
  sheets = null,
  expectedHeaders = [],
  expectedLegacyHeaders = [],
  expectedTemplateSheetName,
}) => {
  const errorsByRow = {};
  const isWorkbookFile = /\.(xlsx|xls)$/i.test(fileName);
  const sheetNames = sheets ? Object.keys(sheets) : [];
  let effectiveHeaders = headers;
  const normalizeHeaders = (values = []) =>
    values.map((header) => normalizeText(header));
  const headersMatch = (actual = [], expected = []) =>
    actual.length === expected.length &&
    expected.every((header, index) => actual[index] === header);

  if (isWorkbookFile && expectedTemplateSheetName) {
    const baseRequiredSheetNames = [
      expectedTemplateSheetName,
      "Instructions",
      "Lookups",
    ];
    const allowedSheetNames = new Set([
      ...baseRequiredSheetNames,
      PRESERVICE_STATUS_SHEET_NAME,
    ]);
    const unexpectedSheetNames = sheetNames.filter(
      (sheetName) => !allowedSheetNames.has(sheetName),
    );
    const missingSheetNames = baseRequiredSheetNames.filter(
      (sheetName) => !sheetNames.includes(sheetName),
    );

    if (missingSheetNames.length > 0) {
      pushError(
        errorsByRow,
        1,
        `The workbook structure was changed. Missing sheet(s): ${missingSheetNames.join(", ")}. Download a fresh protected template and try again.`,
      );
    }

    if (sheetNames.includes(expectedTemplateSheetName)) {
      effectiveHeaders =
        sheets?.[expectedTemplateSheetName]?.header || effectiveHeaders;
    }

    if (unexpectedSheetNames.length > 0) {
      pushError(
        errorsByRow,
        1,
        `Do not add or rename workbook sheets. Unexpected sheet(s): ${unexpectedSheetNames.join(", ")}.`,
      );
    }
  }

  const normalizedActualHeaders = normalizeHeaders(effectiveHeaders);
  const normalizedExpectedHeaders = normalizeHeaders(expectedHeaders);
  const normalizedLegacyHeaders = normalizeHeaders(expectedLegacyHeaders);
  const usesSeparateStatusSheet = headersMatch(
    normalizedActualHeaders,
    normalizedExpectedHeaders,
  );
  const usesLegacyInlineStatuses =
    normalizedLegacyHeaders.length > 0 &&
    headersMatch(normalizedActualHeaders, normalizedLegacyHeaders);
  const hasMatchingHeaders =
    usesSeparateStatusSheet || usesLegacyInlineStatuses;

  if (!hasMatchingHeaders) {
    pushError(
      errorsByRow,
      1,
      "The spreadsheet columns were changed. Keep every template header in its original name and order, without adding columns.",
    );
  }

  if (
    isWorkbookFile &&
    usesSeparateStatusSheet &&
    !sheetNames.includes(PRESERVICE_STATUS_SHEET_NAME)
  ) {
    pushError(
      errorsByRow,
      1,
      `The workbook structure was changed. Missing sheet: ${PRESERVICE_STATUS_SHEET_NAME}. Download a fresh protected template and try again.`,
    );
  }

  if (
    isWorkbookFile &&
    sheetNames.includes(PRESERVICE_STATUS_SHEET_NAME)
  ) {
    const statusHeaders = normalizeHeaders(
      sheets?.[PRESERVICE_STATUS_SHEET_NAME]?.header || [],
    );
    if (
      !headersMatch(
        statusHeaders,
        normalizeHeaders(PRESERVICE_STATUS_TEMPLATE_HEADERS),
      )
    ) {
      pushError(
        errorsByRow,
        1,
        `The ${PRESERVICE_STATUS_SHEET_NAME} columns were changed. Keep every status header in its original name and order.`,
      );
    }
  }

  return {
    errors: errorsByRow,
    hasErrors: Object.keys(errorsByRow).length > 0,
    usesSeparateStatusSheet,
    usesLegacyInlineStatuses,
  };
};

const validateCommonIdentity = ({
  row,
  rowNumber,
  errorsByRow,
  requireNationality = false,
}) => {
  const firstName = normalizeText(
    getRowValue(row, ["firstName", "First Name"]),
  );
  const fatherName = normalizeText(
    getRowValue(row, ["fatherName", "Father Name"]),
  );
  const grandFatherName = normalizeText(
    getRowValue(row, [
      "grandFatherName",
      "Grand Father Name",
      "grandfatherName",
    ]),
  );
  const sex = toTitleCase(getRowValue(row, ["sex", "Sex"]));
  const rawIdNo = getRowValue(row, [
    "idNo",
    "ID No",
    "National ID",
    "National ID (FAN)",
    "nationalId",
  ]);
  const idNo = normalizeFanId(rawIdNo);
  const rawNationality = normalizeText(
    getRowValue(row, ["nationality", "Nationality", "citizenship"]),
  );
  const nationality = rawNationality || DEFAULT_GENERIC_ENTRY_NATIONALITY;

  if (!firstName) pushError(errorsByRow, rowNumber, "First Name is required.");
  if (!fatherName)
    pushError(errorsByRow, rowNumber, "Father Name is required.");
  if (!grandFatherName)
    pushError(errorsByRow, rowNumber, "Grand Father Name is required.");
  if (requireNationality && !rawNationality) {
    pushError(errorsByRow, rowNumber, "Nationality is required.");
  }
  if (!["Male", "Female"].includes(sex)) {
    pushError(errorsByRow, rowNumber, "Sex must be either Male or Female.");
  }
  if (!idNo) {
    pushError(errorsByRow, rowNumber, "National ID (FAN) is required.");
  } else if (!hasValidFanIdFormat(rawIdNo) || !isValidFanId(idNo)) {
    pushError(
      errorsByRow,
      rowNumber,
      "National ID (FAN) must be entered as exactly 16 digits. Format the Excel cell as Text if needed.",
    );
  }

  return {
    firstName,
    fatherName,
    grandFatherName,
    sex,
    idNo,
    nationality,
  };
};

export const getProgramTypeOptions = () =>
  PreserviceProgramTypeEnum.map((item) => item.name);

const GRADUATE_ENTRY_PRESERVICE_LEVEL = "NimeiProgram";

export const TVET_PRESERVICE_LEVEL = "TVETProgram";
export const TVET_BACKGROUND_PROFESSION_OPTIONS = [
  "Grade 10",
  "Grade 12",
  "Level I",
  "Level II",
  "Level III",
  "Level IV",
];
export const TVET_STUDENT_STATUS_OPTIONS = [
  "New",
  "Active",
  "Transfer",
  "Expelled",
  "Drop Out",
  "Graduated",
  "Withdrawn",
];
export const TVET_YEAR_OF_EDUCATION_OPTIONS = [
  "Level I",
  "Level II",
  "Level III",
  "Level IV",
];
const isTvetStructuredPreService = (preServiceLevel) =>
  normalizeLookupValue(preServiceLevel) ===
  normalizeLookupValue(TVET_PRESERVICE_LEVEL);

const isGraduateEntryStructuredPreService = (preServiceLevel) =>
  normalizeLookupValue(preServiceLevel) ===
  normalizeLookupValue(GRADUATE_ENTRY_PRESERVICE_LEVEL);

const matchesGraduateEntryLevelOfStudy = (item = {}) => {
  const combinedValue = [item?.name, item?.code, item?.level_type]
    .map((value) => normalizeText(value).toLowerCase())
    .join(" ");

  return (
    normalizeText(item?.level_type).toLowerCase() === "nimei program" ||
    normalizeText(item?.level_type).toLowerCase() === "graduate entry" ||
    /sub[^a-z0-9]*spec/.test(combinedValue)
  );
};

export const filterGraduateEntryLevelOfStudies = (items = []) => {
  const activeItems = items.filter((item) => item?.isActive !== false);
  const filteredItems = activeItems.filter(matchesGraduateEntryLevelOfStudy);

  if (filteredItems.length === 0) {
    return activeItems;
  }

  const seenIds = new Set();
  return filteredItems.filter((item) => {
    const identifier =
      item?.id ?? `${item?.name}-${item?.code}-${item?.level_type}`;
    if (seenIds.has(identifier)) {
      return false;
    }
    seenIds.add(identifier);
    return true;
  });
};

export const resolveGraduateEntryBackgroundProfession = (items = []) => {
  const activeItems = items.filter((item) => item?.isActive !== false);
  const preferredProfession =
    activeItems.find(
      (item) => normalizeLookupValue(item?.name) === "medicine",
    ) ||
    activeItems.find((item) =>
      /medical\s*doctor|general\s*practitioner|family\s*medicine|medicine/.test(
        normalizeText(item?.name).toLowerCase(),
      ),
    ) ||
    null;

  return {
    preferredProfession,
    options: preferredProfession
      ? [
          preferredProfession,
          ...activeItems.filter((item) => item?.id !== preferredProfession.id),
        ]
      : activeItems,
  };
};

const findOptionValue = (options = [], rawValue) => {
  const normalizedValue = normalizeLookupValue(rawValue);
  if (!normalizedValue) {
    return null;
  }

  return (
    options.find((value) => normalizeLookupValue(value) === normalizedValue) ||
    null
  );
};

export const buildTvetTemplateOptions = ({ lookups = {} } = {}) => {
  const identityOptions = buildSharedIdentityTemplateOptions(lookups);
  const programs = Array.isArray(lookups.educationalPrograms)
    ? [...lookups.educationalPrograms]
    : [];
  const disciplines = Array.isArray(lookups.disciplines)
    ? [...lookups.disciplines]
    : [];
  const levels = Array.isArray(lookups.levelOfStudies)
    ? [...lookups.levelOfStudies]
    : [];
  const yearOptions = [...TVET_YEAR_OF_EDUCATION_OPTIONS];
  const universityOptions = getActiveUniversityOptions(lookups);

  const validProgramIds = new Set(programs.map((program) => program?.id));
  const validDisciplines = disciplines
    .filter((discipline) => validProgramIds.has(discipline?.educationProgramId))
    .sort((left, right) => (left?.name || "").localeCompare(right?.name || ""));

  return {
    ...identityOptions,
    headers: TVET_TEMPLATE_HEADERS,
    sexOptions: ["Male", "Female"],
    backgroundProfessionOptions: [...TVET_BACKGROUND_PROFESSION_OPTIONS],
    programOptions: programs.map((item) => item?.name).filter(Boolean),
    disciplineOptions: [
      ...new Set(validDisciplines.map((item) => item?.name).filter(Boolean)),
    ],
    levelOfStudyOptions: levels.map((item) => item?.name).filter(Boolean),
    yearOfEducationOptions: yearOptions,
    studentStatusOptions: [...TVET_STUDENT_STATUS_OPTIONS],
    universityOptions,
    programHierarchy: { programs, disciplines: validDisciplines },
    missingRequirements: {
      ...identityOptions.identityMissingRequirements,
      hasPrograms: programs.length > 0,
      hasDisciplines: validDisciplines.length > 0,
      hasLevels: levels.length > 0,
      hasYearOfEducation: yearOptions.length > 0,
      hasUniversities: universityOptions.length > 0,
    },
  };
};

export const buildGraduateEntryTemplateOptions = ({ lookups = {} } = {}) => {
  const identityOptions = buildSharedIdentityTemplateOptions(lookups);
  const programs = Array.isArray(lookups.educationalPrograms)
    ? [...lookups.educationalPrograms]
    : [];
  const disciplines = Array.isArray(lookups.disciplines)
    ? [...lookups.disciplines]
    : [];
  const levels = filterGraduateEntryLevelOfStudies(
    lookups.levelOfStudies || [],
  );
  const yearOptions = Array.isArray(lookups.yearOfEducation)
    ? lookups.yearOfEducation.map((item) => item?.name).filter(Boolean)
    : [];
  const studentStatuses = Array.isArray(lookups.studentStatuses)
    ? lookups.studentStatuses.map((item) => item?.name).filter(Boolean)
    : [];
  const backgroundProfessionOptions = resolveGraduateEntryBackgroundProfession(
    lookups.backgroundProfessions || [],
  )
    .options.map((item) => item?.name)
    .filter(Boolean);
  const universityOptions = getActiveUniversityOptions(lookups);

  const validProgramIds = new Set(programs.map((program) => program?.id));
  const validDisciplines = disciplines
    .filter((discipline) => validProgramIds.has(discipline?.educationProgramId))
    .sort((left, right) => (left?.name || "").localeCompare(right?.name || ""));

  return {
    ...identityOptions,
    headers: DETAILED_STRUCTURED_TEMPLATE_HEADERS,
    sexOptions: ["Male", "Female"],
    backgroundProfessionOptions,
    programEducationTypeOptions: programs
      .map((item) => item?.name)
      .filter(Boolean),
    disciplineOptions: [
      ...new Set(validDisciplines.map((item) => item?.name).filter(Boolean)),
    ],
    levelOfStudyOptions: levels.map((item) => item?.name).filter(Boolean),
    yearOfEducationOptions: yearOptions,
    studentStatusOptions: studentStatuses,
    universityOptions,
    programHierarchy: { programs, disciplines: validDisciplines },
    missingRequirements: {
      ...identityOptions.identityMissingRequirements,
      hasBackgroundProfessions: backgroundProfessionOptions.length > 0,
      hasPrograms: programs.length > 0,
      hasDisciplines: validDisciplines.length > 0,
      hasLevels: levels.length > 0,
      hasYearOfEducation: yearOptions.length > 0,
      hasStudentStatuses: studentStatuses.length > 0,
      hasUniversities: universityOptions.length > 0,
    },
  };
};

export const buildDetailedStructuredTemplateOptions = ({ lookups = {} } = {}) => {
  const identityOptions = buildSharedIdentityTemplateOptions(lookups);
  const fallbackYearOfEducation = [
    { name: "Not Applicable" },
    ...YearOfEducEnum,
  ];
  const programs = Array.isArray(lookups.educationalPrograms)
    ? getActiveLookupItems([...lookups.educationalPrograms])
    : [];
  const disciplines = Array.isArray(lookups.disciplines)
    ? getActiveLookupItems([...lookups.disciplines])
    : [];
  const levels = Array.isArray(lookups.levelOfStudies)
    ? getActiveLookupItems([...lookups.levelOfStudies])
    : [];
  const yearOptions = (
    Array.isArray(lookups.yearOfEducation) && lookups.yearOfEducation.length > 0
      ? lookups.yearOfEducation
      : fallbackYearOfEducation
  )
    .map((item) => item?.name)
    .filter(Boolean);
  const studentStatuses = Array.isArray(lookups.studentStatuses)
    ? getUniqueLookupNames(getActiveLookupItems(lookups.studentStatuses))
    : [];
  const backgroundProfessionOptions = Array.isArray(
    lookups.backgroundProfessions,
  )
    ? getUniqueLookupNames(getActiveLookupItems(lookups.backgroundProfessions))
    : [];
  const universityOptions = getActiveUniversityOptions(lookups);

  const validProgramIds = new Set(programs.map((program) => program?.id));
  const validDisciplines = disciplines
    .filter((discipline) => validProgramIds.has(discipline?.educationProgramId))
    .sort((left, right) => (left?.name || "").localeCompare(right?.name || ""));

  return {
    ...identityOptions,
    headers: DETAILED_STRUCTURED_TEMPLATE_HEADERS,
    sexOptions: ["Male", "Female"],
    backgroundProfessionOptions,
    programEducationTypeOptions: getUniqueLookupNames(programs),
    disciplineOptions: getUniqueLookupNames(validDisciplines),
    levelOfStudyOptions: getUniqueLookupNames(levels),
    yearOfEducationOptions: yearOptions,
    studentStatusOptions: studentStatuses,
    universityOptions,
    programHierarchy: { programs, disciplines: validDisciplines },
    missingRequirements: {
      ...identityOptions.identityMissingRequirements,
      hasBackgroundProfessions: backgroundProfessionOptions.length > 0,
      hasPrograms: programs.length > 0,
      hasDisciplines: validDisciplines.length > 0,
      hasLevels: levels.length > 0,
      hasYearOfEducation: yearOptions.length > 0,
      hasStudentStatuses: studentStatuses.length > 0,
      hasUniversities: universityOptions.length > 0,
    },
  };
};

export const buildGenericEntryTemplateOptions = ({ lookups = {} } = {}) => {
  const programs = Array.isArray(lookups.educationalPrograms)
    ? getActiveLookupItems([...lookups.educationalPrograms])
    : [];
  const disciplines = Array.isArray(lookups.disciplines)
    ? getActiveLookupItems([...lookups.disciplines])
    : [];
  const levels = Array.isArray(lookups.levelOfStudies)
    ? getActiveLookupItems([...lookups.levelOfStudies])
    : [];
  const yearOptions = Array.isArray(lookups.yearOfEducation)
    ? lookups.yearOfEducation.map((item) => item?.name).filter(Boolean)
    : [];
  const studentStatuses = Array.isArray(lookups.studentStatuses)
    ? getUniqueLookupNames(getActiveLookupItems(lookups.studentStatuses))
    : [];
  const ethnicities = Array.isArray(lookups.ethnicities)
    ? getUniqueLookupNames(getActiveLookupItems(lookups.ethnicities))
    : [];
  const birthRegions = Array.isArray(lookups.birthRegions)
    ? getUniqueLookupNames(getActiveLookupItems(lookups.birthRegions))
    : [];
  const birthZones = Array.isArray(lookups.birthZones)
    ? getUniqueLookupNames(getActiveLookupItems(lookups.birthZones))
    : [];
  const birthWoredas = Array.isArray(lookups.birthWoredas)
    ? getUniqueLookupNames(getActiveLookupItems(lookups.birthWoredas))
    : [];
  const defaultLevelOfStudy = getGenericEntryDefaultLevel(levels);
  const universityOptions = getActiveUniversityOptions(lookups);

  const validProgramIds = new Set(programs.map((program) => program?.id));
  const validDisciplines = disciplines
    .filter((discipline) => validProgramIds.has(discipline?.educationProgramId))
    .sort((left, right) => (left?.name || "").localeCompare(right?.name || ""));

  const missingRequirements = {
    hasPrograms: programs.length > 0,
    hasDisciplines: validDisciplines.length > 0,
    hasLevels: levels.length > 0,
    hasYearOfEducation: yearOptions.length > 0,
    hasStudentStatuses: studentStatuses.length > 0,
    hasEthnicities: ethnicities.length > 0,
    hasBirthRegions: birthRegions.length > 0,
    hasBirthZones: birthZones.length > 0,
    hasBirthWoredas: birthWoredas.length > 0,
    hasUniversities: universityOptions.length > 0,
  };

  return {
    headers: GENERIC_ENTRY_TEMPLATE_HEADERS,
    sexOptions: ["Male", "Female"],
    nationalityDefault: DEFAULT_GENERIC_ENTRY_NATIONALITY,
    ethnicityOptions: ethnicities,
    birthRegionOptions: birthRegions,
    birthZoneOptions: birthZones,
    birthWoredaOptions: birthWoredas,
    birthHierarchy: {
      regions: getActiveLookupItems(lookups.birthRegions || []),
      zones: getActiveLookupItems(lookups.birthZones || []),
      woredas: getActiveLookupItems(lookups.birthWoredas || []),
    },
    programOptions: getUniqueLookupNames(programs),
    disciplineOptions: getUniqueLookupNames(validDisciplines),
    levelOfStudyOptions: getUniqueLookupNames(levels),
    defaultLevelOfStudyName: defaultLevelOfStudy?.name || "",
    yearOfEducationOptions: yearOptions,
    studentStatusOptions: studentStatuses,
    universityOptions,
    programHierarchy: { programs, disciplines: validDisciplines },
    missingRequirements,
  };
};

const buildStudentStatusSheetIndex = ({
  rows = [],
  yearOfEducationOptions = [],
  studentStatusOptions = [],
  errorsByRow,
}) => {
  const statusesByFan = new Map();
  const rowNumbersByFan = new Map();
  const duplicateKeys = new Set();

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const errorKey = `${PRESERVICE_STATUS_SHEET_NAME} ${rowNumber}`;
    const rawFan = getRowValue(row, [
      "idNo",
      "National ID (FAN)",
      "National ID",
      "FAN",
    ]);
    const fan = normalizeFanId(rawFan);
    const yearOfEducation = resolveYearOfEducationValue(
      getRowValue(row, ["yearOfEducation", "Year of Education"]),
      yearOfEducationOptions,
    );
    const studentStatus = findOptionValue(
      studentStatusOptions,
      getRowValue(row, ["studentStatus", "Student Status"]),
    );
    const gpa = parseNumber(getRowValue(row, ["gpa", "GPA"]));
    const cumulativeGpa = parseNumber(
      getRowValue(row, ["cumulativeGpa", "Cumulative GPA", "CGPA"]),
    );

    if (!fan || !hasValidFanIdFormat(rawFan) || !isValidFanId(fan)) {
      pushError(
        errorsByRow,
        errorKey,
        "National ID (FAN) must be entered as exactly 16 digits.",
      );
    }
    if (
      !yearOfEducation ||
      !yearOfEducationOptions.includes(yearOfEducation)
    ) {
      pushError(
        errorsByRow,
        errorKey,
        `Year of Education must match one of: ${formatAllowedValues(yearOfEducationOptions)}.`,
      );
    }
    if (!studentStatus) {
      pushError(
        errorsByRow,
        errorKey,
        `Student Status must match one of: ${formatAllowedValues(studentStatusOptions)}.`,
      );
    }
    if (gpa === null || gpa?.invalid) {
      pushError(
        errorsByRow,
        errorKey,
        "GPA is required and must be numeric.",
      );
    } else if (gpa < 0 || gpa > 4) {
      pushError(errorsByRow, errorKey, "GPA must be between 0 and 4.");
    }
    if (cumulativeGpa === null || cumulativeGpa?.invalid) {
      pushError(
        errorsByRow,
        errorKey,
        "Cumulative GPA is required and must be numeric.",
      );
    } else if (cumulativeGpa < 0 || cumulativeGpa > 4) {
      pushError(
        errorsByRow,
        errorKey,
        "Cumulative GPA must be between 0 and 4.",
      );
    }

    if (errorsByRow[errorKey]?.length) {
      return;
    }

    const duplicateKey = `${fan}|${normalizeLookupValue(yearOfEducation)}`;
    if (duplicateKeys.has(duplicateKey)) {
      pushError(
        errorsByRow,
        errorKey,
        "A student can have only one status for each Year of Education.",
      );
      return;
    }
    duplicateKeys.add(duplicateKey);

    const statuses = statusesByFan.get(fan) || [];
    statuses.push({
      yearOfEducation,
      studentStatus,
      gpa,
      cumulativeGpa,
    });
    statusesByFan.set(fan, statuses);

    const rowNumbers = rowNumbersByFan.get(fan) || [];
    rowNumbers.push(rowNumber);
    rowNumbersByFan.set(fan, rowNumbers);
  });

  return { statusesByFan, rowNumbersByFan };
};

const addOrphanStudentStatusErrors = ({
  errorsByRow,
  rowNumbersByFan,
  studentFans,
}) => {
  rowNumbersByFan.forEach((rowNumbers, fan) => {
    if (studentFans.has(fan)) {
      return;
    }
    rowNumbers.forEach((rowNumber) => {
      pushError(
        errorsByRow,
        `${PRESERVICE_STATUS_SHEET_NAME} ${rowNumber}`,
        `No student row matches National ID (FAN) ${fan}.`,
      );
    });
  });
};

export const validateGenericEntryImport = ({
  rows = [],
  statusRows = null,
  lookups = {},
  currentYear = new Date().getFullYear(),
}) => {
  const errorsByRow = {};
  const validRows = [];
  const yearOfEducationOptions = (lookups.yearOfEducation || []).map(
    (item) => item?.name,
  );
  const studentStatusOptions = (lookups.studentStatuses || []).map(
    (item) => item?.name,
  );
  const defaultLevelOfStudy = getGenericEntryDefaultLevel(
    lookups.levelOfStudies || [],
  );
  const maxBirthDate = `${currentYear - 17}-12-31`;
  const usesSeparateStatusSheet = Array.isArray(statusRows);
  const statusSheetIndex = usesSeparateStatusSheet
    ? buildStudentStatusSheetIndex({
        rows: statusRows,
        yearOfEducationOptions,
        studentStatusOptions,
        errorsByRow,
      })
    : { statusesByFan: new Map(), rowNumbersByFan: new Map() };
  const studentFans = new Set();

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const identity = validateCommonIdentity({
      row,
      rowNumber,
      errorsByRow,
      requireNationality: usesSeparateStatusSheet,
    });
    const dateOfBirth = toIsoDateString(
      getRowValue(row, ["dateOfBirth", "Date of Birth", "Birth Date"]),
    );
    const entryYear = parseYear(
      getRowValue(row, ["entryYear", "Year of Entry"]),
    );
    const graduationYear = parseYear(
      getRowValue(row, ["graduationYear", "Year of Graduation"]),
    );
    const yearOfEducation = resolveYearOfEducationValue(
      getRowValue(row, ["yearOfEducation", "Year of Education"]),
      yearOfEducationOptions,
    );
    const studentStatus = normalizeText(
      getRowValue(row, ["studentStatus", "Student Status"]),
    );
    const gpa = parseNumber(
      getRowValue(row, ["gpa", "GPA", "semesterGpa", "Semester GPA"]),
    );
    const cumulativeGpa = parseNumber(
      getRowValue(row, ["cumulativeGpa", "Cumulative GPA", "cgpa", "CGPA"]),
    );
    const universityInput = getRowValue(row, [
      "universityId",
      "Higher Learning Institution",
      "University",
    ]);
    const activeUniversities = getActiveLookupItems(
      lookups.universities || [],
    );
    const university =
      findLookupItem(activeUniversities, universityInput) ||
      (!normalizeText(universityInput) && activeUniversities.length === 1
        ? activeUniversities[0]
        : null);
    if (identity.idNo) {
      studentFans.add(identity.idNo);
    }
    const ethnicity = findLookupItem(
      lookups.ethnicities,
      getRowValue(row, ["ethnicity", "Ethnicity"]),
    );
    const birthRegion = findLookupItem(
      lookups.birthRegions,
      getRowValue(row, ["birthRegionId", "Birth Region", "Region"]),
    );
    const birthZone = findLookupItem(
      lookups.birthZones,
      getRowValue(row, ["birthZoneId", "Birth Zone", "Zone"]),
    );
    const birthWoreda = findLookupItem(
      lookups.birthWoredas,
      getRowValue(row, ["birthWoredaId", "Birth Woreda", "Woreda"]),
    );
    const birthTownVillage = normalizeText(
      getRowValue(row, [
        "birthTownVillage",
        "Town / Village",
        "Birth Town / Village",
        "Town/Village",
      ]),
    );
    const program = findLookupItem(
      lookups.educationalPrograms,
      getRowValue(row, [
        "educationProgramId",
        "educationProgram",
        "Program Education Type",
        "Program Type",
        "Program Education",
        "Program",
        "Program Mode",
      ]),
    );
    const discipline = findProgramDiscipline(
      lookups.disciplines,
      program,
      getRowValue(row, [
        "disciplineId",
        "discipline",
        "Department / Discipline",
        "Department",
      ]),
    );
    const levelOfStudyInput = getRowValue(row, [
      "levelOfStudyId",
      "levelOfStudy",
      "Level Of Study",
      "levelOfEducation",
    ]);
    const levelOfStudy =
      findLookupItem(lookups.levelOfStudies, levelOfStudyInput) ||
      (!normalizeText(levelOfStudyInput) ? defaultLevelOfStudy : null);
    const expectedYearRaw = parseYear(
      getRowValue(row, [
        "expectedYear",
        "Expected Graduation Year",
        "expectedYearOfGraduation",
        "proposedYearOfGraduation",
      ]),
    );

    const birthDateError = getBirthDateValidationError(
      dateOfBirth,
      maxBirthDate,
    );
    if (birthDateError) {
      pushError(errorsByRow, rowNumber, birthDateError);
    }
    if (entryYear === null || entryYear?.invalid) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Entry must be a valid 4-digit year.",
      );
    } else if (entryYear < 1950 || entryYear > currentYear) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Entry must be between 1950 and the current year.",
      );
    }

    if (graduationYear?.invalid) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Graduation must be a valid 4-digit year.",
      );
    }

    if (!program) {
      pushError(
        errorsByRow,
        rowNumber,
        "Program Education Type must match an active educational program.",
      );
    }
    if (!discipline) {
      pushError(
        errorsByRow,
        rowNumber,
        "Department / Discipline must match an active discipline.",
      );
    }
    if (!levelOfStudy) {
      pushError(
        errorsByRow,
        rowNumber,
        "Level Of Study must match an active level of study.",
      );
    }
    if (program && discipline && discipline.educationProgramId != program.id) {
      pushError(
        errorsByRow,
        rowNumber,
        "Department / Discipline does not belong to the selected program.",
      );
    }

    if (!usesSeparateStatusSheet) {
      if (
        !yearOfEducation ||
        !yearOfEducationOptions.includes(yearOfEducation)
      ) {
        pushError(
          errorsByRow,
          rowNumber,
          `Year of Education must match one of: ${formatAllowedValues(yearOfEducationOptions)}.`,
        );
      }
      if (!studentStatus || !studentStatusOptions.includes(studentStatus)) {
        pushError(
          errorsByRow,
          rowNumber,
          `Student Status must match one of: ${formatAllowedValues(studentStatusOptions)}.`,
        );
      }
    }
    if (!ethnicity) {
      pushError(
        errorsByRow,
        rowNumber,
        "Ethnicity must match an active ethnicity.",
      );
    }
    if (usesSeparateStatusSheet && !university) {
      pushError(
        errorsByRow,
        rowNumber,
        "Higher Learning Institution must match an active institution.",
      );
    }
    if (!birthRegion) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Region must match an active region.",
      );
    }
    if (!birthZone) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Zone must match an active zone.",
      );
    }
    if (!birthWoreda) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Woreda must match an active woreda.",
      );
    }
    if (birthRegion && birthZone && birthZone.regionId != birthRegion.id) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Zone does not belong to the selected Birth Region.",
      );
    }
    if (birthZone && birthWoreda && birthWoreda.zoneId != birthZone.id) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Woreda does not belong to the selected Birth Zone.",
      );
    }
    if (!birthTownVillage) {
      pushError(errorsByRow, rowNumber, "Town / Village is required.");
    }
    if (!usesSeparateStatusSheet) {
      if (gpa === null || gpa?.invalid) {
        pushError(
          errorsByRow,
          rowNumber,
          "GPA is required and must be numeric.",
        );
      } else if (gpa < 0 || gpa > 4) {
        pushError(errorsByRow, rowNumber, "GPA must be between 0 and 4.");
      }
      if (cumulativeGpa === null || cumulativeGpa?.invalid) {
        pushError(
          errorsByRow,
          rowNumber,
          "Cumulative GPA is required and must be numeric.",
        );
      } else if (cumulativeGpa < 0 || cumulativeGpa > 4) {
        pushError(
          errorsByRow,
          rowNumber,
          "Cumulative GPA must be between 0 and 4.",
        );
      }
    }

    let expectedYear = expectedYearRaw;
    if (expectedYear?.invalid) {
      pushError(
        errorsByRow,
        rowNumber,
        "Expected Graduation Year must be a valid 4-digit year.",
      );
      expectedYear = null;
    }

    if (
      !expectedYear &&
      typeof entryYear === "number" &&
      discipline?.disciplineYear != null
    ) {
      expectedYear = entryYear + Number(discipline.disciplineYear);
    }

    if (!expectedYear) {
      pushError(
        errorsByRow,
        rowNumber,
        "Expected Graduation Year is required.",
      );
    }
    if (
      typeof expectedYear === "number" &&
      typeof entryYear === "number" &&
      discipline?.disciplineYear != null &&
      expectedYear !== entryYear + Number(discipline.disciplineYear)
    ) {
      pushError(
        errorsByRow,
        rowNumber,
        `Expected Graduation Year must equal Year of Entry + ${Number(discipline.disciplineYear)} for the selected discipline.`,
      );
    }
    if (
      typeof graduationYear === "number" &&
      typeof expectedYear === "number" &&
      graduationYear < expectedYear
    ) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Graduation cannot be earlier than Expected Graduation Year.",
      );
    }

    if (errorsByRow[rowNumber]?.length) {
      return;
    }

    validRows.push({
      ...identity,
      dateOfBirth,
      educationProgramId: program.id,
      disciplineId: discipline.id,
      levelOfStudyId: levelOfStudy.id,
      universityId: university?.id || null,
      entryYear,
      expectedYear,
      graduationYear:
        typeof graduationYear === "number" ? graduationYear : null,
      preServiceLevel: "GenericEntry",
      nationality: identity.nationality || DEFAULT_GENERIC_ENTRY_NATIONALITY,
      ethnicity: ethnicity.name,
      birthRegionId: birthRegion.id,
      birthZoneId: birthZone.id,
      birthWoredaId: birthWoreda.id,
      birthTownVillage,
      studentStatuses: usesSeparateStatusSheet
        ? statusSheetIndex.statusesByFan.get(identity.idNo) || []
        : [
            {
              yearOfEducation,
              studentStatus,
              gpa,
              cumulativeGpa,
            },
          ],
    });
  });

  if (usesSeparateStatusSheet) {
    addOrphanStudentStatusErrors({
      errorsByRow,
      rowNumbersByFan: statusSheetIndex.rowNumbersByFan,
      studentFans,
    });
  }

  return finalizeValidation(validRows, errorsByRow);
};

export const validateStructuredPreServiceImport = ({
  rows = [],
  statusRows = null,
  preServiceLevel,
  lookups = {},
  currentYear = new Date().getFullYear(),
}) => {
  const errorsByRow = {};
  const validRows = [];
  const programTypes = getProgramTypeOptions();
  const isTvetProgram = isTvetStructuredPreService(preServiceLevel);
  const fallbackYearOfEducation = [
    { name: "Not Applicable" },
    ...YearOfEducEnum,
  ];
  const yearOfEducationOptions = isTvetProgram
    ? [...TVET_YEAR_OF_EDUCATION_OPTIONS]
    : (
        lookups.yearOfEducation && lookups.yearOfEducation.length > 0
          ? lookups.yearOfEducation
          : fallbackYearOfEducation
      )
        .map((item) => item?.name)
        .filter(Boolean);
  const isGraduateEntryProgram =
    isGraduateEntryStructuredPreService(preServiceLevel);
  const isPostBasicProgram = preServiceLevel === "PostBasic";
  const isPostGraduateProgram = preServiceLevel === "PostGraduate";
  const usesDetailedStudentStatuses =
    isGraduateEntryProgram || isPostBasicProgram || isPostGraduateProgram;
  const preferredGraduateEntryProfession =
    resolveGraduateEntryBackgroundProfession(
      lookups.backgroundProfessions || [],
    ).preferredProfession;
  const studentStatusOptions = (lookups.studentStatuses || [])
    .map((item) => item?.name)
    .filter(Boolean);
  const usesSeparateStatusSheet = Array.isArray(statusRows);
  const statusSheetIndex = usesSeparateStatusSheet
    ? buildStudentStatusSheetIndex({
        rows: statusRows,
        yearOfEducationOptions,
        studentStatusOptions: isTvetProgram
          ? TVET_STUDENT_STATUS_OPTIONS
          : studentStatusOptions,
        errorsByRow,
      })
    : { statusesByFan: new Map(), rowNumbersByFan: new Map() };
  const studentFans = new Set();

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const identity = validateCommonIdentity({
      row,
      rowNumber,
      errorsByRow,
      requireNationality: usesSeparateStatusSheet,
    });
    if (identity.idNo) {
      studentFans.add(identity.idNo);
    }
    const ethnicity = findLookupItem(
      lookups.ethnicities,
      getRowValue(row, ["ethnicity", "Ethnicity"]),
    );
    const birthRegion = findLookupItem(
      lookups.birthRegions,
      getRowValue(row, ["birthRegionId", "Birth Region", "Region"]),
    );
    const birthZone = findLookupItem(
      lookups.birthZones,
      getRowValue(row, ["birthZoneId", "Birth Zone", "Zone"]),
    );
    const birthWoreda = findLookupItem(
      lookups.birthWoredas,
      getRowValue(row, ["birthWoredaId", "Birth Woreda", "Woreda"]),
    );
    const birthTownVillage = normalizeText(
      getRowValue(row, [
        "birthTownVillage",
        "Town / Village",
        "Birth Town / Village",
        "Town/Village",
      ]),
    );
    const entryYear = parseYear(
      getRowValue(row, ["entryYear", "Year of Entry"]),
    );
    const expectedYear = parseYear(
      getRowValue(row, [
        "expectedYear",
        "Expected Graduation Year",
        "expectedYearOfGraduation",
      ]),
    );
    const graduationYear = parseYear(
      getRowValue(row, ["graduationYear", "Year of Graduation"]),
    );
    const programType = normalizeText(
      getRowValue(row, [
        "admissionClassification",
        "Program Type",
        "Program Education Type",
        "Program Education",
        "Program Mode",
        "programMode",
        "programType",
      ]),
    );
    const withdrawalStatus = toTitleCase(
      getRowValue(row, ["isWithdrawn", "Withdrawal Status", "withdrawn"]),
    );
    const program = findLookupItem(
      lookups.educationalPrograms,
      getRowValue(row, [
        "educationProgramId",
        "educationProgram",
        "Program Education Type",
        "Program Type",
        "Program Education",
        "Program",
        "Program Mode",
      ]),
    );
    const discipline = findProgramDiscipline(
      lookups.disciplines,
      program,
      getRowValue(row, [
        "department",
        "discipline",
        "Department / Discipline",
        "Department",
      ]),
    );
    const levelOfStudy = findLookupItem(
      lookups.levelOfStudies,
      getRowValue(row, [
        "levelOfStudyId",
        "levelOfStudy",
        "Level Of Study",
        "levelOfEducation",
      ]),
    );
    const backgroundProfessionValue = isTvetProgram
      ? findOptionValue(
          TVET_BACKGROUND_PROFESSION_OPTIONS,
          getRowValue(row, [
            "backgroundProfession",
            "Background Profession",
            "Back groud profession",
          ]),
        )
      : null;
    const backgroundProfession = isTvetProgram
      ? backgroundProfessionValue
      : findLookupItem(
          lookups.backgroundProfessions,
          getRowValue(row, [
            "backgroundProfession",
            "Background Profession",
            "Back Ground Profession",
          ]),
          { fallbackKeys: ["profession"] },
        );
    const resolvedBackgroundProfession =
      isGraduateEntryProgram && !backgroundProfession
        ? preferredGraduateEntryProfession
        : backgroundProfession;
    const rawYearOfEducation = resolveYearOfEducationValue(
      getRowValue(row, ["yearOfEducation", "Year of Education"]),
      yearOfEducationOptions,
    );
    const yearOfEducation =
      rawYearOfEducation ||
      (!isTvetProgram && usesDetailedStudentStatuses
        ? "Not Applicable"
        : rawYearOfEducation);
    const rawStudentStatus = getRowValue(row, [
      "studentStatus",
      "Student Status",
      "isWithdrawn",
      "Withdrawal Status",
      "withdrawn",
    ]);
    const studentStatus = isTvetProgram
      ? findOptionValue(
          TVET_STUDENT_STATUS_OPTIONS,
          getRowValue(row, ["studentStatus", "Student Status"]),
        )
      : normalizeLegacyWithdrawalStatus(rawStudentStatus);
    const gpa = parseNumber(getRowValue(row, ["gpa", "GPA"]));
    const cumulativeGpa = parseNumber(
      getRowValue(row, ["cumulativeGpa", "Cumulative GPA", "CGPA"]),
    );
    const universityInput = getRowValue(row, [
      "universityId",
      "Higher Learning Institution",
      "University",
    ]);
    const activeUniversities = getActiveLookupItems(
      lookups.universities || [],
    );
    const university =
      findLookupItem(activeUniversities, universityInput) ||
      (!normalizeText(universityInput) && activeUniversities.length === 1
        ? activeUniversities[0]
        : null);
    const minimumExpectedYearOffset =
      isPostGraduateProgram && discipline?.disciplineYear != null
        ? Math.max(1, Number(discipline.disciplineYear))
        : 2;

    if (entryYear === null || entryYear?.invalid) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Entry must be a valid 4-digit year.",
      );
    } else if (entryYear < 1950 || entryYear > currentYear) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Entry must be between 1950 and the current year.",
      );
    }

    if (expectedYear === null || expectedYear?.invalid) {
      pushError(
        errorsByRow,
        rowNumber,
        "Expected Graduation Year must be a valid 4-digit year.",
      );
    }
    if (
      typeof expectedYear === "number" &&
      typeof entryYear === "number" &&
      expectedYear < entryYear + minimumExpectedYearOffset
    ) {
      pushError(
        errorsByRow,
        rowNumber,
        `Expected Graduation Year must be at least entry year + ${minimumExpectedYearOffset}.`,
      );
    }
    if (graduationYear?.invalid) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Graduation must be a valid 4-digit year.",
      );
    }
    if (
      typeof graduationYear === "number" &&
      typeof expectedYear === "number" &&
      graduationYear < expectedYear
    ) {
      pushError(
        errorsByRow,
        rowNumber,
        "Year of Graduation cannot be earlier than Expected Graduation Year.",
      );
    }

    if (!ethnicity) {
      pushError(
        errorsByRow,
        rowNumber,
        "Ethnicity must match an active ethnicity.",
      );
    }
    if (usesSeparateStatusSheet && !university) {
      pushError(
        errorsByRow,
        rowNumber,
        "Higher Learning Institution must match an active institution.",
      );
    }
    if (!birthRegion) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Region must match an active region.",
      );
    }
    if (!birthZone) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Zone must match an active zone.",
      );
    }
    if (!birthWoreda) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Woreda must match an active woreda.",
      );
    }
    if (birthRegion && birthZone && birthZone.regionId != birthRegion.id) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Zone does not belong to the selected Birth Region.",
      );
    }
    if (birthZone && birthWoreda && birthWoreda.zoneId != birthZone.id) {
      pushError(
        errorsByRow,
        rowNumber,
        "Birth Woreda does not belong to the selected Birth Zone.",
      );
    }
    if (!birthTownVillage) {
      pushError(errorsByRow, rowNumber, "Town / Village is required.");
    }

    if (!resolvedBackgroundProfession) {
      pushError(
        errorsByRow,
        rowNumber,
        isTvetProgram
          ? `Background Profession must be one of: ${TVET_BACKGROUND_PROFESSION_OPTIONS.join(", ")}.`
          : "Background Profession must match an active health profession.",
      );
    }
    if (!program) {
      pushError(
        errorsByRow,
        rowNumber,
        "Program Education Type must match an active educational program.",
      );
    }
    if (!discipline) {
      pushError(
        errorsByRow,
        rowNumber,
        "Department / Discipline must match an active discipline.",
      );
    }
    if (!levelOfStudy) {
      pushError(
        errorsByRow,
        rowNumber,
        "Level Of Study must match an active level of study.",
      );
    }
    if (program && discipline && discipline.educationProgramId != program.id) {
      pushError(
        errorsByRow,
        rowNumber,
        "Department / Discipline does not belong to the selected program.",
      );
    }
    const resolvedProgramType = programType || null;
    if (
      !isTvetProgram &&
      !usesDetailedStudentStatuses &&
      !programTypes.includes(resolvedProgramType)
    ) {
      pushError(
        errorsByRow,
        rowNumber,
        `Program Education Type must be one of: ${programTypes.join(", ")}.`,
      );
    }
    if (
      !isTvetProgram &&
      !usesDetailedStudentStatuses &&
      !["Yes", "No"].includes(withdrawalStatus)
    ) {
      pushError(
        errorsByRow,
        rowNumber,
        "Withdrawal Status must be either Yes or No.",
      );
    }
    if (!usesSeparateStatusSheet && isTvetProgram) {
      if (
        !yearOfEducation ||
        !yearOfEducationOptions.includes(yearOfEducation)
      ) {
        pushError(
          errorsByRow,
          rowNumber,
          `Year of Education must match one of: ${formatAllowedValues(yearOfEducationOptions)}.`,
        );
      }
      if (!studentStatus) {
        pushError(
          errorsByRow,
          rowNumber,
          `Student Status must be one of: ${formatAllowedValues(TVET_STUDENT_STATUS_OPTIONS)}.`,
        );
      }
      if (gpa === null || gpa === undefined) {
        pushError(
          errorsByRow,
          rowNumber,
          "GPA is required and must be numeric.",
        );
      } else if (gpa?.invalid) {
        pushError(errorsByRow, rowNumber, "GPA must be numeric.");
      } else if (gpa < 0 || gpa > 4) {
        pushError(errorsByRow, rowNumber, "GPA must be between 0 and 4.");
      }
      if (cumulativeGpa === null || cumulativeGpa?.invalid) {
        pushError(
          errorsByRow,
          rowNumber,
          "Cumulative GPA is required and must be numeric.",
        );
      } else if (cumulativeGpa < 0 || cumulativeGpa > 4) {
        pushError(
          errorsByRow,
          rowNumber,
          "Cumulative GPA must be between 0 and 4.",
        );
      }
    } else if (!usesSeparateStatusSheet && usesDetailedStudentStatuses) {
      if (
        !yearOfEducation ||
        !yearOfEducationOptions.includes(yearOfEducation)
      ) {
        pushError(
          errorsByRow,
          rowNumber,
          `Year of Education must match one of: ${formatAllowedValues(yearOfEducationOptions)}.`,
        );
      }
      if (!studentStatus || !studentStatusOptions.includes(studentStatus)) {
        pushError(
          errorsByRow,
          rowNumber,
          `Student Status must match one of: ${formatAllowedValues(studentStatusOptions)}.`,
        );
      }
      if (gpa === null || gpa === undefined) {
        pushError(
          errorsByRow,
          rowNumber,
          "GPA is required and must be numeric.",
        );
      } else if (gpa?.invalid) {
        pushError(errorsByRow, rowNumber, "GPA must be numeric.");
      } else if (typeof gpa === "number" && (gpa < 0 || gpa > 4)) {
        pushError(errorsByRow, rowNumber, "GPA must be between 0 and 4.");
      }
      if (cumulativeGpa === null || cumulativeGpa === undefined) {
        pushError(
          errorsByRow,
          rowNumber,
          "Cumulative GPA is required and must be numeric.",
        );
      } else if (cumulativeGpa?.invalid) {
        pushError(errorsByRow, rowNumber, "Cumulative GPA must be numeric.");
      } else if (
        typeof cumulativeGpa === "number" &&
        (cumulativeGpa < 0 || cumulativeGpa > 4)
      ) {
        pushError(
          errorsByRow,
          rowNumber,
          "Cumulative GPA must be between 0 and 4.",
        );
      }
    }

    if (errorsByRow[rowNumber]?.length) {
      return;
    }

    validRows.push({
      ...identity,
      nationality: identity.nationality || DEFAULT_GENERIC_ENTRY_NATIONALITY,
      ethnicity: ethnicity.name,
      birthRegionId: birthRegion.id,
      birthZoneId: birthZone.id,
      birthWoredaId: birthWoreda.id,
      birthTownVillage,
      backgroundProfession: isTvetProgram
        ? resolvedBackgroundProfession
        : resolvedBackgroundProfession.name,
      educationProgramId: program.id,
      disciplineId: discipline.id,
      department: discipline.name,
      levelOfStudyId: levelOfStudy.id,
      universityId: university?.id || null,
      entryYear,
      expectedYear,
      graduationYear:
        typeof graduationYear === "number" ? graduationYear : null,
      admissionClassification:
        isTvetProgram || isGraduateEntryProgram
          ? null
          : isPostGraduateProgram
            ? program.name
            : resolvedProgramType,
      isWithdrawn: isTvetProgram
        ? null
        : usesSeparateStatusSheet
          ? statusSheetIndex.statusesByFan.get(identity.idNo)?.slice(-1)[0]
              ?.studentStatus || null
          : usesDetailedStudentStatuses
            ? studentStatus
            : withdrawalStatus,
      studentStatuses: usesSeparateStatusSheet
        ? statusSheetIndex.statusesByFan.get(identity.idNo) || []
        : isTvetProgram
          ? [
              {
                yearOfEducation,
                studentStatus,
                gpa,
                cumulativeGpa,
              },
            ]
          : usesDetailedStudentStatuses
            ? [
                {
                  yearOfEducation,
                  studentStatus,
                  gpa: typeof gpa === "number" ? gpa : null,
                  cumulativeGpa:
                    typeof cumulativeGpa === "number" ? cumulativeGpa : null,
                },
              ]
            : [],
      preServiceLevel,
    });
  });

  if (usesSeparateStatusSheet) {
    addOrphanStudentStatusErrors({
      errorsByRow,
      rowNumbersByFan: statusSheetIndex.rowNumbersByFan,
      studentFans,
    });
  }

  return finalizeValidation(validRows, errorsByRow);
};
